/**
 * Tiny strongly-typed event emitter.
 *
 * Replaces the C#-style `event EventHandler<TArgs>` pattern used throughout
 * `shared-application-code/Shared/Assets/Campfire/Runtime/Models/**` (see
 * `IProjectModel.cs` and `ISceneModel.cs` for examples). Prefer this over
 * ad-hoc callbacks so that models, tools, and UI can subscribe/unsubscribe
 * consistently and without coupling to concrete classes.
 */

export type Handler<T> = (payload: T) => void;

export class Emitter<T = void> {
  private readonly m_handlers = new Set<Handler<T>>();

  on(handler: Handler<T>): () => void {
    this.m_handlers.add(handler);
    return () => this.off(handler);
  }

  off(handler: Handler<T>): void {
    this.m_handlers.delete(handler);
  }

  emit(payload: T): void {
    for (const h of this.m_handlers) {
      try {
        h(payload);
      } catch (err) {
        console.error("[Emitter] handler threw:", err);
      }
    }
  }

  clear(): void {
    this.m_handlers.clear();
  }

  get size(): number {
    return this.m_handlers.size;
  }
}
